"use client";

import * as React from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styled from "styled-components";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("ko");

interface MonthRangeSelectorProps {
  /** 6개월 범위의 마지막 월 */
  endDate: dayjs.Dayjs;
  /** 날짜가 변경될 때 호출되는 함수 */
  onDateChange: (date: dayjs.Dayjs) => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0;
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${({ disabled }) => (disabled ? "#434859" : "#e0e1e8")};
  font-size: 20px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:active {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const RangeText = styled.div`
  flex: 1;
  text-align: center;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: -0.2px;
  line-height: 20px;
  margin: 0 8px;
  white-space: nowrap;
`;

export function MonthRangeSelector({
  endDate,
  onDateChange,
}: MonthRangeSelectorProps) {
  const today = dayjs();

  // 1. 표시할 날짜 범위 계산
  const startDate = React.useMemo(
    () => endDate.subtract(5, "month"),
    [endDate]
  );

  // "2025.11 - 2026.4" 형식으로 표기
  const displayRange = React.useMemo(() => {
    const startYear = startDate.year();
    const startMonth = String(startDate.month() + 1).padStart(2, "0"); // dayjs는 0-indexed
    const endYear = endDate.year();
    const endMonth = String(endDate.month() + 1).padStart(2, "0");

    return `${startYear}.${startMonth} - ${endYear}.${endMonth}`;
  }, [startDate, endDate]);

  const handlePrevious = () => {
    onDateChange(endDate.subtract(1, "month"));
  };

  const handleNext = () => {
    const nextMonth = endDate.add(1, "month");
    if (nextMonth.isSameOrBefore(today, "month")) {
      onDateChange(nextMonth);
    }
  };

  const isNextDisabled = endDate.isSame(today, "month");

  return (
    <Container>
      <NavButton onClick={handlePrevious} aria-label="이전 6개월">
        <ChevronLeft size={24} />
      </NavButton>
      <RangeText>{displayRange}</RangeText>
      <NavButton
        onClick={handleNext}
        disabled={isNextDisabled}
        aria-label="다음 6개월"
      >
        <ChevronRight size={24} />
      </NavButton>
    </Container>
  );
}
